import React, { useEffect, useMemo, useRef, useState } from "react";
import ServiceRemoteSelect from "./ServiceRemoteSelect";
import {
  serviceFieldHints,
  serviceKindFieldHints,
  serviceSectionTitles,
} from "./serviceCatalogPresentation";
import "./serviceCatalog.css";
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Header,
  Icon,
  Input,
  Label,
  Menu,
  Message,
  Modal,
  Segment,
  Table,
  TextArea,
} from "semantic-ui-react";
import DateTextBox from "react-lib/apps/common/DateTextBox";
import { formatComma } from "react-lib/utils/tsUtils";
import { formatADtoBEString } from "react-lib/utils/dateUtils";
import {
  FieldSchema,
  Metadata,
  SelectOption,
  childMetadata,
  fieldOptions,
  isFieldEnabled,
  editPricingFields,
} from "./serviceKindSchema";
import {
  CatalogForm,
  RecordData,
  RemoteFieldState,
  errorText,
  pricingErrorSummary,
  pricingStatus,
  stripDivisionPrefix,
  formatServiceEndDate,
} from "./sequence/ServiceCatalog";

export type SchemaFieldsProps = {
  fields: FieldSchema[];
  metadata: Metadata;
  values: RecordData;
  errors?: RecordData;
  masters: Record<string, SelectOption[]>;
  masterErrors?: Record<string, string>;
  disabled?: boolean;
  editing?: boolean;
  kind?: string;
  remoteFields?: Record<string, RemoteFieldState>;
  remotePrefix?: string;
  onRemoteSearch?: (field: string, query: string) => void;
  onChange: (key: string, value: any) => void;
  onImage?: (image: File | null) => void;
  pricing?: boolean;
  pricingEditor?: boolean;
  productImages?: RecordData[];
  productImagesError?: string;
  division?: any;
  scrollToErrors?: boolean;
  canCreatePackage?: boolean;
};
let nextSchemaFieldsId = 0;
const hasFieldError = (value: any): boolean =>
  Array.isArray(value)
    ? value.some(hasFieldError)
    : value && typeof value === "object"
    ? Object.values(value).some(hasFieldError)
    : !!value;
export const SchemaFields = (props: SchemaFieldsProps) => {
  const root = useRef<HTMLDivElement>(null);
  const [idPrefix] = useState(() => `service-catalog-fields-${++nextSchemaFieldsId}`);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [errorTarget, setErrorTarget] = useState<string | null>(null);
  const sections = useMemo(() => {
    const visibleFields = props.fields.filter(
      (field) =>
        isFieldEnabled(field) &&
        (!field.createPackageOnly || (!props.editing && props.canCreatePackage)) &&
        (field.uiOnly || props.metadata[field.key]) &&
        (!field.showWhen || props.values[field.showWhen])
    );
    const closingFields = new Set([
      "description",
      "active_flag",
      "is_affiliate_exclusive",
      "is_vat_applicable",
      "create_package",
      "has_lots",
      "overall_cost",
    ]);
    const orderedFields = props.kind
      ? [
          ...visibleFields.filter((field) => !closingFields.has(field.key)),
          ...visibleFields.filter((field) => closingFields.has(field.key)),
        ]
      : visibleFields;
    const sections: { key: string; title?: string; fields: FieldSchema[]; panel: boolean }[] = [];
    orderedFields.forEach((field) => {
      const key =
        props.kind && closingFields.has(field.key) ? "closing" : field.section || "fields";
      let section = sections[sections.length - 1];
      if (!section || section.key !== key) {
        const panel = !!props.kind && key !== "ข้อมูลทั่วไป" && key !== "closing";
        section = {
          key,
          panel,
          title: panel
            ? key === "ข้อมูลเฉพาะประเภท"
              ? serviceSectionTitles[props.kind!] || key
              : key
            : undefined,
          fields: [],
        };
        sections.push(section);
      }
      section.fields.push(field);
    });
    return sections;
  }, [
    props.fields,
    props.metadata,
    props.values,
    props.kind,
    props.editing,
    props.canCreatePackage,
  ]);
  useEffect(() => {
    if (props.scrollToErrors === false || !props.errors) return;
    const errors = props.errors;
    const erroredFields = sections
      .flatMap((section) => section.fields)
      .filter((field) => hasFieldError(errors[field.key]));
    const firstField = erroredFields[0];
    const firstEditableField = erroredFields.find(
      (field) =>
        !props.disabled &&
        !field.readOnly &&
        !props.metadata[field.key]?.read_only &&
        !(props.editing && field.key === "code") &&
        !(field.options && "master" in field.options && props.masterErrors?.[field.options.master])
    );
    if (!firstField) return;
    if (props.kind === "DRUG") {
      setExpandedSections((previous) => ({
        ...previous,
        ...Object.fromEntries(
          sections
            .filter(
              (section) =>
                section.panel && section.fields.some((field) => hasFieldError(errors[field.key]))
            )
            .map((section) => [section.key, true])
        ),
      }));
    }
    setErrorTarget((firstEditableField || firstField).key);
  }, [props.errors, props.kind, props.scrollToErrors, sections]);
  useEffect(() => {
    if (!errorTarget) return;
    const field = Array.from(
      root.current?.querySelectorAll<HTMLElement>("[data-field]") || []
    ).find((element) => element.dataset.field === errorTarget);
    if (!field || field.closest("[hidden]")) return;
    const target = field.querySelector<HTMLElement>(".field.error") || field;
    target.scrollIntoView?.({ block: "center", behavior: "smooth" });
    target
      .querySelector<HTMLElement>(
        'input:not([disabled]):not([readonly]):not([type="hidden"]):not([tabindex="-1"]), textarea:not([disabled]):not([readonly]), [role="combobox"]:not([aria-disabled="true"])'
      )
      ?.focus({ preventScroll: true });
    setErrorTarget(null);
  }, [errorTarget, expandedSections]);
  const renderField = (field: FieldSchema, panel: boolean) => {
    const metadata = props.metadata[field.key] || {};
    const value = props.values[field.key];
    const remoteKey = props.remotePrefix ? `${props.remotePrefix}.${field.key}` : field.key;
    const masterError =
      field.options && "master" in field.options
        ? props.masterErrors?.[field.options.master]
        : undefined;
    const disabled =
      !!masterError ||
      props.disabled ||
      field.readOnly ||
      metadata.read_only ||
      (props.editing && field.key === "code");
    const error = props.errors?.[field.key];
    const common = {
      disabled: !!disabled,
      name: field.key,
      "aria-label": field.label,
      "aria-required": !!metadata.required && !disabled,
      placeholder: field.placeholder || `ระบุ${field.label}`,
    };
    const options = fieldOptions(field, metadata, props.masters, props.kind).map((option) =>
      props.pricingEditor && field.key === "bill_mode" && option.code
        ? { ...option, text: `[${option.code}] ${option.text}` }
        : option
    );
    const selected = Array.isArray(value) ? value : [value];
    selected
      .filter(
        (id) =>
          id !== undefined &&
          id !== null &&
          id !== "" &&
          !options.some((option) => option.value === id)
      )
      .forEach((id) => {
        const originalOption =
          field.options && "master" in field.options
            ? props.masters[field.options.master]?.find((option) => option.value === id)
            : undefined;
        options.push(originalOption || { key: id, value: id, text: String(id) });
      });
    let control: React.ReactNode;
    switch (field.input) {
      case "remoteSelect":
        control = (
          <ServiceRemoteSelect
            label={field.label}
            value={value}
            disabled={!!disabled}
            required={!!metadata.required}
            multiple={field.multiple}
            state={props.remoteFields?.[remoteKey]}
            onSearch={(query) => props.onRemoteSearch?.(remoteKey, query)}
            onChange={(next) => props.onChange(field.key, next)}
          />
        );
        break;
      case "textList":
        control = (
          <div className="service-catalog-text-list">
            {(value || []).map((text: string, index: number) => (
              <Form.Field key={index} className="service-catalog-text-row" error={!!error?.[index]}>
                <Input
                  aria-label={`${field.label} ${index + 1}`}
                  value={text}
                  disabled={!!disabled}
                  maxLength={metadata.child?.max_length}
                  onChange={(_, data) =>
                    props.onChange(
                      field.key,
                      value.map((item: string, position: number) =>
                        position === index ? data.value : item
                      )
                    )
                  }
                />
                {!disabled && (
                  <Button
                    type="button"
                    aria-label={`ลบ${field.label} ${index + 1}`}
                    onClick={() =>
                      props.onChange(
                        field.key,
                        value.filter((_: string, position: number) => position !== index)
                      )
                    }
                  >
                    ×
                  </Button>
                )}
                {error?.[index] && <Message negative>{errorText(error[index])}</Message>}
              </Form.Field>
            ))}
            {!disabled && (
              <Button
                type="button"
                onClick={() => props.onChange(field.key, [...(value || []), ""])}
                className="service-catalog-add-link"
              >
                + เพิ่ม {field.label} อีก
              </Button>
            )}
          </div>
        );
        break;
      case "select":
      case "multiselect":
        control = (
          <Dropdown
            {...common}
            fluid
            search
            selection
            placeholder={`-- เลือก${field.label} --`}
            clearable={!disabled && !metadata.required}
            multiple={field.input === "multiselect"}
            renderLabel={
              disabled ? (item) => ({ content: item.text, removeIcon: null }) : undefined
            }
            options={options}
            value={value ?? (field.input === "multiselect" ? [] : "")}
            onChange={(_, data) => props.onChange(field.key, data.value)}
          />
        );
        break;
      case "checkbox":
        control = (
          <Checkbox
            {...common}
            checked={!!value}
            label={field.label}
            onChange={(_, data) => props.onChange(field.key, !!data.checked)}
          />
        );
        break;
      case "textarea":
        control = (
          <TextArea
            {...common}
            value={value ?? ""}
            maxLength={metadata.max_length}
            onChange={(_, data) => props.onChange(field.key, data.value)}
          />
        );
        break;
      case "date":
        control = (
          <DateTextBox
            {...common}
            closeOnComplete={props.pricing}
            input={{ "aria-label": field.label }}
            value={
              value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? formatADtoBEString(value) : value || ""
            }
            error={!!error}
            placeholder="วว/ดด/ปปปป (พ.ศ.)"
            onChange={(date) => props.onChange(field.key, date)}
          />
        );
        break;
      case "rows":
        control = (
          <Segment>
            {(value || []).map((row: RecordData, index: number) => (
              <Segment key={index}>
                <SchemaFields
                  fields={field.fields || []}
                  scrollToErrors={false}
                  remoteFields={props.remoteFields}
                  remotePrefix={
                    props.remotePrefix ? `${props.remotePrefix}.${field.key}` : field.key
                  }
                  onRemoteSearch={props.onRemoteSearch}
                  metadata={childMetadata(metadata)}
                  values={row}
                  errors={error?.[index]}
                  masters={props.masters}
                  masterErrors={props.masterErrors}
                  disabled={disabled}
                  onChange={(key, next) =>
                    props.onChange(
                      field.key,
                      value.map((item: RecordData, position: number) =>
                        position === index ? { ...item, [key]: next } : item
                      )
                    )
                  }
                />
                {!disabled && (
                  <Button
                    type="button"
                    size="small"
                    onClick={() =>
                      props.onChange(
                        field.key,
                        value.filter((_: any, position: number) => position !== index)
                      )
                    }
                  >
                    ลบส่วนประกอบ
                  </Button>
                )}
              </Segment>
            ))}
            {!disabled && (
              <Button
                type="button"
                onClick={() =>
                  props.onChange(field.key, [
                    ...(value || []),
                    { sequence: (value || []).length + 1 },
                  ])
                }
              >
                เพิ่มส่วนประกอบยา
              </Button>
            )}
          </Segment>
        );
        break;
      case "image":
        control = props.editing ? (
          <div>
            {props.productImagesError ? (
              <Message warning role="status">
                {props.productImagesError}
              </Message>
            ) : props.productImages?.length ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                {props.productImages.map((image, index) => (
                  <img
                    key={image.id ?? index}
                    src={image.image || image.thumbnail}
                    alt={`รูปยา ${index + 1}`}
                    style={{ maxWidth: "100%", width: 160, height: 160, objectFit: "contain" }}
                  />
                ))}
              </div>
            ) : (
              <p>ยังไม่มีรูปยา</p>
            )}
          </div>
        ) : (
          <input
            {...common}
            type="file"
            accept="image/*"
            onChange={(event) => props.onImage?.(event.target.files?.[0] || null)}
          />
        );
        break;
      default:
        control = (
          <Input
            {...common}
            fluid
            value={value ?? ""}
            maxLength={metadata.max_length}
            type={field.input === "number" || field.input === "decimal" ? "number" : "text"}
            label={
              props.pricing && field.key.startsWith("price_")
                ? { basic: true, content: "บาท" }
                : undefined
            }
            labelPosition={props.pricing && field.key.startsWith("price_") ? "right" : undefined}
            step={
              field.input === "decimal"
                ? metadata.decimal_places == null
                  ? "any"
                  : 10 ** -metadata.decimal_places
                : field.input === "number"
                ? 1
                : undefined
            }
            min={metadata.min_value}
            max={metadata.max_value}
            onChange={(_, data) =>
              props.onChange(
                field.key,
                field.input === "number" && data.value !== "" ? Number(data.value) : data.value
              )
            }
          />
        );
    }
    const hint =
      (props.kind && serviceKindFieldHints[props.kind]?.[field.key]) ||
      serviceFieldHints[field.key];
    const fullWidth = props.pricingEditor
      ? field.key === "bill_mode"
      : (!panel && !props.pricing) ||
        field.input === "textarea" ||
        field.input === "rows" ||
        field.input === "textList" ||
        field.input === "checkbox" ||
        field.input === "remoteSelect" ||
        field.input === "image" ||
        ["mode", "manufacturer", "bill_mode", "start_date", "end_date", "organs"].includes(
          field.key
        );
    return (
      <Form.Field
        key={field.key}
        data-field={field.key}
        className={`service-catalog-field${fullWidth ? " service-catalog-field-wide" : ""}${
          field.input === "checkbox" ? " service-catalog-check-field" : ""
        }`}
        required={!!metadata.required && (props.pricingEditor || !disabled)}
        error={!!error}
      >
        {field.input !== "checkbox" && (
          <label>
            {field.label}
            {field.key === "room_type" && (
              <span className="service-catalog-selection-count">
                {Array.isArray(value) && value.length
                  ? `เลือกแล้ว ${value.length} รายการ`
                  : "ยังไม่ได้เลือก"}
              </span>
            )}
          </label>
        )}
        {field.key === "code" && props.kind ? (
          <div className="service-catalog-code-input">
            <Input
              aria-label="หน่วยงานเจ้าของรายการ"
              value={props.division ?? "–"}
              readOnly
              tabIndex={-1}
              className="service-catalog-division-prefix"
            />
            <span aria-hidden="true">-</span>
            {control}
          </div>
        ) : (
          control
        )}
        {!props.pricingEditor && hint && <p className="service-catalog-field-hint">{hint}</p>}
        {masterError && (
          <Message warning size="small" role="status">
            {masterError}
          </Message>
        )}
        {error && (
          <Message negative size="small">
            {errorText(error, !props.pricingEditor)}
          </Message>
        )}
      </Form.Field>
    );
  };
  const renderSubsections = (fields: FieldSchema[], panel: boolean) => {
    const groups: { title?: string; card?: boolean; fields: FieldSchema[] }[] = [];
    fields.forEach((field) => {
      let group = groups[groups.length - 1];
      if (!group || group.title !== field.subsection) {
        group = { title: field.subsection, card: field.subsectionCard, fields: [] };
        groups.push(group);
      }
      group.fields.push(field);
    });
    return groups.map((group, index) => (
      <div
        key={index}
        role={group.card ? "group" : undefined}
        aria-label={group.card ? group.title : undefined}
        className={group.card ? "service-catalog-subsection-card" : "service-catalog-subsection"}
      >
        {group.title && !group.card && (
          <h5 className="service-catalog-subsection-title">{group.title}</h5>
        )}
        <div className="service-catalog-field-grid">
          {group.fields.map((field) => renderField(field, panel))}
        </div>
      </div>
    ));
  };
  const renderSection = (section: (typeof sections)[number]) => {
    const collapsible = props.kind === "DRUG" && section.panel;
    const open = !!expandedSections[section.key];
    const sectionId = `${idPrefix}-${sections.indexOf(section)}`;
    return (
      <section
        key={section.key}
        className={
          section.panel
            ? `service-catalog-section${collapsible ? " service-catalog-accordion-section" : ""}`
            : "service-catalog-plain-section"
        }
      >
        {section.title && (
          <Header as="h4">
            {collapsible ? (
              <button
                type="button"
                id={`${sectionId}-title`}
                className="service-catalog-section-toggle"
                aria-expanded={open}
                aria-controls={sectionId}
                onClick={() =>
                  setExpandedSections((previous) => ({
                    ...previous,
                    [section.key]: !previous[section.key],
                  }))
                }
              >
                <span>{section.title}</span>
                <Icon name={open ? "chevron up" : "chevron down"} aria-hidden="true" />
              </button>
            ) : (
              section.title
            )}
          </Header>
        )}
        <div
          id={collapsible ? sectionId : undefined}
          aria-labelledby={collapsible ? `${sectionId}-title` : undefined}
          className="service-catalog-section-body"
          hidden={collapsible && !open}
        >
          {renderSubsections(section.fields, section.panel)}
        </div>
      </section>
    );
  };
  return (
    <Form
      as="div"
      className={`service-catalog-fields${props.pricing ? " service-catalog-pricing-fields" : ""}${
        props.pricingEditor ? " service-catalog-price-panel-fields" : ""
      }`}
    >
      <div ref={root}>
        {props.kind === "DRUG" ? (
          <>
            {sections
              .filter((section) => !section.panel && section.key !== "closing")
              .map(renderSection)}
            {!!sections.filter((section) => section.panel).length && (
              <Segment
                color="blue"
                role="region"
                aria-label="ข้อมูลเฉพาะของยา"
                style={{ border: "1px solid #2185d0" }}
                className="service-catalog-drug-sections"
              >
                <div className="service-catalog-drug-header">
                  <Header as="h3">ข้อมูลยา (Drug)</Header>
                  <div className="service-catalog-section-actions">
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedSections(
                          Object.fromEntries(
                            sections
                              .filter((section) => section.panel)
                              .map((section) => [section.key, true])
                          )
                        )
                      }
                    >
                      ขยายทั้งหมด
                    </button>
                    <span aria-hidden="true">/</span>
                    <button type="button" onClick={() => setExpandedSections({})}>
                      ย่อทั้งหมด
                    </button>
                  </div>
                </div>
                {sections.filter((section) => section.panel).map(renderSection)}
              </Segment>
            )}
            {sections.filter((section) => section.key === "closing").map(renderSection)}
          </>
        ) : (
          sections.map(renderSection)
        )}
      </div>
    </Form>
  );
};

type Props = {
  form: CatalogForm;
  canCreatePackage?: boolean;
  label: string;
  fields: FieldSchema[];
  masters: Record<string, SelectOption[]>;
  masterErrors?: Record<string, string>;
  values: RecordData;
  pricingRows: RecordData[];
  newPricing: RecordData;
  pricingFields: FieldSchema[];
  noPrice: boolean;
  errors: RecordData;
  loading: boolean;
  onClose: () => void;
  onTab: (tab: number) => void;
  onChange: (key: string, value: any) => void;
  onImage: (file: File | null) => void;
  onNoPrice: (value: boolean) => void;
  onPricingRows: (rows: RecordData[]) => void;
  onNewPricing: (key: string, value: any) => void;
  onSave: () => void;
  onAddPricing: () => void;
  onRemoteSearch: (field: string, query: string) => void;
  onTogglePricing: (value: boolean) => void;
  onSelectPricing: (id: any) => void;
  editPricing?: RecordData;
  editPricingMetadata?: Metadata;
  newPricingMetadata?: Metadata;
  editPricingErrors?: RecordData;
  onEditPricing?: (key: string, value: any) => void;
  onCopyPricing?: (row: RecordData) => void;
  onDeletePricing?: (row: RecordData) => void;
};
const CardServiceFormUX = (props: Props) => {
  const { form } = props;
  const disabled = form.readOnly || props.loading;
  const selectedPricing = form.pricings.find((row) => row.id === form.selectedPricing);
  const pricingPeriod = (row: RecordData) =>
    `${formatADtoBEString(row.start_date)} – ${formatServiceEndDate(row.end_date)}`;
  const statusBadge = (row: RecordData) => {
    const status = pricingStatus(row);
    return (
      <Label
        size="tiny"
        className={`service-catalog-pricing-status status-${
          status === "ใช้อยู่" ? "current" : status === "ล่วงหน้า" ? "future" : "expired"
        }`}
      >
        {status}
      </Label>
    );
  };
  const editingMetadata = props.editPricingMetadata || form.pricingMetadata;
  const addingMetadata = props.newPricingMetadata || form.pricingMetadata;
  const editSummary = pricingErrorSummary(
    props.editPricingErrors,
    editPricingFields.filter((field) => editingMetadata[field.key])
  );
  const addSummary = pricingErrorSummary(
    props.errors,
    editPricingFields.filter((field) => addingMetadata[field.key])
  );
  const values = {
    ...props.values,
    ...(form.original
      ? { code: stripDivisionPrefix(form.original.code, form.original.of_division) }
      : {}),
  };
  return (
    <Modal
      open
      size="large"
      className="service-catalog-modal service-catalog-form-modal"
      dimmer={{ className: "service-catalog-dimmer" }}
      aria-labelledby="service-catalog-form-title"
      onClose={props.loading ? undefined : props.onClose}
      closeOnDimmerClick={false}
      closeOnEscape={false}
    >
      <Modal.Header>
        <h2 id="service-catalog-form-title" title={props.label}>
          {form.original
            ? form.readOnly
              ? "รายละเอียดบริการ"
              : "แก้ไขบริการ"
            : "สร้างรายการบริการใหม่"}
        </h2>
        <Menu pointing secondary>
          {["รายละเอียดสินค้า", "ตั้งค่าราคา"].map((label, index) => (
            <Menu.Item
              key={label}
              name={label}
              active={form.tab === index}
              disabled={props.loading}
              onClick={() => props.onTab(index)}
            />
          ))}
        </Menu>
      </Modal.Header>
      <Modal.Content scrolling>
        {!!Object.keys(props.errors).length && !(form.original && form.tab === 1) && (
          <Message negative>{errorText(props.errors)}</Message>
        )}
        {form.tab === 0 ? (
          <SchemaFields
            fields={props.fields}
            metadata={form.metadata}
            masters={props.masters}
            masterErrors={props.masterErrors}
            values={values}
            errors={props.errors}
            disabled={disabled}
            editing={!!form.original}
            canCreatePackage={props.canCreatePackage}
            kind={form.kind}
            division={
              form.original
                ? form.original.of_division?.id ?? form.original.of_division
                : form.division
            }
            remoteFields={form.remoteFields}
            productImages={form.productImages}
            productImagesError={form.productImagesError}
            onRemoteSearch={props.onRemoteSearch}
            onChange={props.onChange}
            onImage={props.onImage}
          />
        ) : (
          <>
            {form.original ? (
              <>
                <div className="service-catalog-pricing-toolbar" role="group" aria-label="ช่วงราคา">
                  <Header as="h3">
                    ช่วงราคา{" "}
                    <span className="service-catalog-required" aria-hidden="true">
                      *
                    </span>
                  </Header>
                  {!form.readOnly && !form.addingPricing && (
                    <Button
                      type="button"
                      basic
                      className="service-catalog-add-pricing"
                      disabled={disabled}
                      icon="plus"
                      content="เพิ่มราคาใหม่"
                      onClick={() => props.onTogglePricing(true)}
                    />
                  )}
                </div>
                {!form.addingPricing && addSummary && (
                  <Message negative role="alert">
                    {addSummary}
                  </Message>
                )}
                <div className="service-catalog-price-table">
                  <Table unstackable compact selectable className="service-catalog-pricing-table">
                    <Table.Header>
                      <Table.Row>
                        {[
                          "ช่วงเวลา",
                          "Bill mode",
                          "ปกติ",
                          "พิเศษ",
                          "พรีเมียม",
                          "ต่างชาติ",
                          "สถานะ",
                          "",
                        ].map((label) => (
                          <Table.HeaderCell key={label}>{label}</Table.HeaderCell>
                        ))}
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {form.pricings.map((row, index) => (
                        <Table.Row
                          key={row.id ?? index}
                          active={form.selectedPricing === row.id}
                          onClick={() => {
                            if (!props.loading) props.onSelectPricing(row.id);
                          }}
                        >
                          <Table.Cell>
                            {formatADtoBEString(row.start_date)} –{" "}
                            {formatServiceEndDate(row.end_date)}
                          </Table.Cell>
                          <Table.Cell>
                            {row.bill_mode_code
                              ? `[${row.bill_mode_code}]`
                              : row.bill_mode_name ||
                                props.masters.billMode?.find(
                                  (option) => option.value === row.bill_mode
                                )?.text ||
                                row.bill_mode}
                          </Table.Cell>
                          {["price_normal", "price_special", "price_premium", "price_foreign"].map(
                            (key) => (
                              <Table.Cell key={key} textAlign="right">
                                {row[key] == null ? "–" : formatComma(row[key])}
                              </Table.Cell>
                            )
                          )}
                          <Table.Cell>{statusBadge(row)}</Table.Cell>
                          <Table.Cell className="service-catalog-pricing-actions">
                            {!form.readOnly && (
                              <>
                                <Button
                                  type="button"
                                  icon="copy outline"
                                  size="mini"
                                  disabled={disabled}
                                  aria-label={`คัดลอกช่วงราคา ${pricingPeriod(row)}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    props.onCopyPricing?.(row);
                                  }}
                                />
                                {pricingStatus(row) === "ล่วงหน้า" && (
                                  <Button
                                    type="button"
                                    icon="trash alternate outline"
                                    size="mini"
                                    disabled={disabled}
                                    aria-label={`ลบช่วงราคา ${pricingPeriod(row)}`}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      props.onDeletePricing?.(row);
                                    }}
                                  />
                                )}
                              </>
                            )}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                      {!form.pricings.length && (
                        <Table.Row>
                          <Table.Cell colSpan={8} textAlign="center">
                            ไม่มีช่วงราคา
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </Table.Body>
                  </Table>
                </div>
                {form.pricingTableError && (
                  <Message negative role="alert" aria-label="ข้อผิดพลาดตารางช่วงราคา">
                    {form.pricingTableError}
                  </Message>
                )}
                {selectedPricing && !form.addingPricing && (
                  <Segment
                    className="service-catalog-pricing-editor"
                    role="region"
                    aria-label="แก้ไขช่วงราคา"
                  >
                    <div className="service-catalog-pricing-editor-header">
                      <Header as="h4">แก้ไขช่วงราคา</Header>
                      <span className="service-catalog-pricing-period">
                        {pricingPeriod(selectedPricing)}
                      </span>
                      {statusBadge(selectedPricing)}
                    </div>
                    {pricingStatus(selectedPricing) === "หมดอายุ" && (
                      <Message info>ช่วงราคาที่หมดอายุแล้วแก้ไขไม่ได้</Message>
                    )}
                    {!!editSummary && (
                      <Message negative role="alert" aria-label="ข้อผิดพลาดช่วงราคา">
                        {editSummary}
                      </Message>
                    )}
                    <SchemaFields
                      pricing
                      pricingEditor
                      fields={editPricingFields}
                      metadata={editingMetadata}
                      masters={props.masters}
                      masterErrors={props.masterErrors}
                      values={props.editPricing || selectedPricing}
                      errors={props.editPricingErrors}
                      disabled={disabled || pricingStatus(selectedPricing) === "หมดอายุ"}
                      onChange={(key, value) => props.onEditPricing?.(key, value)}
                    />
                  </Segment>
                )}
                {form.addingPricing && (
                  <Segment
                    className="service-catalog-pricing-panel"
                    role="region"
                    aria-label="เพิ่มราคาใหม่"
                  >
                    <Header as="h4">เพิ่มราคาใหม่</Header>
                    <p className="service-catalog-pricing-description">
                      กรอกรายละเอียดแล้วกด "บันทึกราคาใหม่" เพื่อเพิ่มลงในตารางช่วงราคา
                    </p>
                    {!!addSummary && (
                      <Message negative role="alert">
                        {addSummary}
                      </Message>
                    )}
                    <SchemaFields
                      pricing
                      pricingEditor
                      fields={editPricingFields}
                      metadata={addingMetadata}
                      masters={props.masters}
                      masterErrors={props.masterErrors}
                      values={props.newPricing}
                      errors={props.errors}
                      disabled={disabled}
                      onChange={props.onNewPricing}
                    />
                    <div className="service-catalog-pricing-panel-actions">
                      <Button
                        type="button"
                        basic
                        disabled={props.loading}
                        onClick={() => props.onTogglePricing(false)}
                      >
                        ยกเลิก
                      </Button>
                      <Button
                        type="button"
                        primary
                        loading={props.loading}
                        disabled={disabled}
                        onClick={props.onAddPricing}
                      >
                        บันทึกราคาใหม่
                      </Button>
                    </div>
                  </Segment>
                )}
              </>
            ) : (
              <>
                <div className="service-catalog-no-price">
                  <Checkbox
                    label="รายการนี้ไม่มีราคา"
                    aria-label="รายการนี้ไม่มีราคา"
                    checked={props.noPrice}
                    disabled={disabled}
                    onChange={(_, data) => props.onNoPrice(!!data.checked)}
                  />
                  <p className="service-catalog-field-hint">
                    เลือกเมื่อเป็นรายการที่ไม่คิดเงิน ระบบจะไม่บังคับกรอกราคาและช่วงเวลาขาย
                  </p>
                </div>
                {!props.noPrice && (
                  <>
                    {props.pricingRows.map((row, index) => (
                      <Segment key={index} className="service-catalog-pricing-card">
                        {props.pricingRows.length > 1 && (
                          <Header as="h4">ช่วงราคาที่ {index + 1}</Header>
                        )}
                        <SchemaFields
                          pricing
                          fields={props.pricingFields}
                          metadata={form.pricingMetadata}
                          masters={props.masters}
                          masterErrors={props.masterErrors}
                          values={row}
                          errors={props.errors.full_pricings?.[index]}
                          disabled={disabled}
                          onChange={(key, value) =>
                            props.onPricingRows(
                              props.pricingRows.map((item, position) =>
                                position === index ? { ...item, [key]: value } : item
                              )
                            )
                          }
                        />
                        {props.pricingRows.length > 1 && (
                          <Button
                            type="button"
                            disabled={disabled}
                            onClick={() =>
                              props.onPricingRows(
                                props.pricingRows.filter((_, position) => position !== index)
                              )
                            }
                          >
                            ลบช่วงราคา
                          </Button>
                        )}
                      </Segment>
                    ))}
                    <Button
                      type="button"
                      disabled={disabled}
                      icon="plus"
                      content="เพิ่มช่วงราคา"
                      onClick={() => props.onPricingRows([...props.pricingRows, {}])}
                    />
                  </>
                )}
              </>
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button
          type="button"
          className="service-catalog-close"
          disabled={props.loading}
          onClick={props.onClose}
        >
          ปิดหน้าจอ
        </Button>
        {!form.readOnly && (
          <Button
            type="button"
            primary
            disabled={props.loading}
            loading={props.loading}
            onClick={props.onSave}
          >
            บันทึกข้อมูล
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
};
export default CardServiceFormUX;
